import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Assignment as InvoiceIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  Store as StoreIcon,
  CalendarToday as DateIcon,
  Undo as ReturnIcon,
  Description as A4Icon,
  LocalPrintshop as ThermalIcon
} from '@mui/icons-material';
import { Sale } from '../../utils/types';
import { formatCurrency } from '../../utils/numberUtils';
import { useReactToPrint } from 'react-to-print';

interface InvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  sale: Sale | null;
  onReturn?: (saleId: string) => void;
  showReturnButton?: boolean;
}

const InvoiceDialog: React.FC<InvoiceDialogProps> = ({
  open,
  onClose,
  sale,
  onReturn,
  showReturnButton = true
}) => {
  const [showReturnAlert, setShowReturnAlert] = useState(false);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [showPrintSuccess, setShowPrintSuccess] = useState(false);
  const [printType, setPrintType] = useState<'thermal' | 'a4'>(() => {
    // استرجاع تفضيل المستخدم من localStorage
    const savedPrintType = localStorage.getItem('pos-print-type');
    return (savedPrintType as 'thermal' | 'a4') || 'a4';
  });
  const invoiceRef = useRef<HTMLDivElement>(null);
  const thermalRef = useRef<HTMLDivElement>(null);

  // دالة الطباعة A4
  const handleA4Print = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: `فاتورة رقم ${sale?.invoiceNumber || sale?.id?.substring(0, 8) || 'N/A'}`,
    onAfterPrint: () => {
      setShowPrintSuccess(true);
      setTimeout(() => setShowPrintSuccess(false), 3000);
    },
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          font-family: 'Arial', sans-serif;
          direction: rtl;
          text-align: right;
          font-size: 11px;
          line-height: 1.3;
        }
        .no-print { display: none !important; }
        .thermal-only { display: none !important; }
        .a4-only { display: block !important; }

        /* تحسينات لضمان صفحة واحدة */
        .MuiPaper-root {
          box-shadow: none !important;
          margin: 0 !important;
          padding: 8px !important;
        }
        .MuiGrid-container {
          margin: 0 !important;
        }
        .MuiGrid-item {
          padding: 4px !important;
        }
        .MuiTableCell-root {
          padding: 4px 8px !important;
          font-size: 10px !important;
        }
        .MuiTypography-h5 {
          font-size: 16px !important;
        }
        .MuiTypography-h6 {
          font-size: 14px !important;
        }
        .MuiTypography-body1 {
          font-size: 11px !important;
        }
        .MuiTypography-body2 {
          font-size: 10px !important;
        }
        .MuiBox-root {
          margin: 4px 0 !important;
        }
      }
    `
  });

  // دالة الطباعة الحرارية
  const handleThermalPrint = useReactToPrint({
    contentRef: thermalRef,
    documentTitle: `فاتورة حرارية رقم ${sale?.id?.substring(0, 8) || 'N/A'}`,
    onAfterPrint: () => {
      setShowPrintSuccess(true);
      setTimeout(() => setShowPrintSuccess(false), 3000);
    },
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 2mm;
      }
      @media print {
        body {
          font-family: 'Arial', sans-serif;
          direction: rtl;
          text-align: center;
          font-size: 10px;
          width: 76mm;
        }
        .no-print { display: none !important; }
        .a4-only { display: none !important; }
        .thermal-only { display: block !important; }
      }
    `
  });

  // فتح dialog خيارات الطباعة
  const handlePrintClick = () => {
    setShowPrintOptions(true);
  };

  // تنفيذ الطباعة حسب النوع المختار
  const handleConfirmPrint = () => {
    // حفظ تفضيل المستخدم
    localStorage.setItem('pos-print-type', printType);

    setShowPrintOptions(false);
    if (printType === 'thermal') {
      handleThermalPrint();
    } else {
      handleA4Print();
    }
  };

  const handleReturn = () => {
    if (sale && onReturn) {
      onReturn(sale.id);
      setShowReturnAlert(true);
      setTimeout(() => setShowReturnAlert(false), 3000);
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'cash': return 'نقدي';
      case 'card': return 'بطاقة ائتمان';
      case 'debt': return 'آجل';
      case 'mixed': return 'مختلط';
      default: return method;
    }
  };

  const getPaymentStatusLabel = (status: string) => {
    switch (status) {
      case 'paid': return 'مدفوع';
      case 'pending': return 'معلق';
      case 'partial': return 'جزئي';
      case 'debt': return 'دين';
      default: return status;
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'partial': return 'info';
      case 'debt': return 'error';
      default: return 'default';
    }
  };

  if (!sale) return null;

  const invoiceDate = new Date(sale.createdAt).toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6">
              فاتورة البيع
            </Typography>
          </Box>
          <IconButton onClick={onClose} className="no-print">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box ref={invoiceRef} sx={{ p: 2 }}>
          {/* رأس الفاتورة */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <InvoiceIcon />
                  <Typography variant="h5" fontWeight="bold">
                    فاتورة بيع
                  </Typography>
                </Box>
                <Typography variant="h6">
                  رقم الفاتورة: {sale.invoiceNumber || sale.id.substring(0, 8)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} textAlign={{ xs: 'left', md: 'right' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <DateIcon />
                  <Typography variant="body1">
                    {invoiceDate}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <StoreIcon />
                  <Typography variant="body1">
                    الفرع: {sale.branchId}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* معلومات العميل والموظف */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  معلومات العميل
                </Typography>
                <Typography variant="body2">
                  <strong>الاسم:</strong> {sale.customerName || 'عميل نقدي'}
                </Typography>
                {sale.customerId && (
                  <Typography variant="body2">
                    <strong>رقم العميل:</strong> {sale.customerPhone || sale.customerId.substring(0, 8)}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  معلومات الموظف
                </Typography>
                <Typography variant="body2">
                  <strong>الموظف:</strong> {sale.employeeName}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* جدول المنتجات */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>المنتج</strong></TableCell>
                  <TableCell align="center"><strong>الكمية</strong></TableCell>
                  <TableCell align="center"><strong>السعر</strong></TableCell>
                  <TableCell align="center"><strong>الإجمالي</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sale.items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          كود: {item.sku}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="center">{formatCurrency(item.price)}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(item.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ملخص المبالغ */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {/* معلومات الدفع */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  معلومات الدفع
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>طريقة الدفع:</strong>
                    </Typography>
                    <Chip 
                      label={getPaymentMethodLabel(sale.paymentMethod)} 
                      size="small" 
                      color="primary" 
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>حالة الدفع:</strong>
                    </Typography>
                    <Chip 
                      label={getPaymentStatusLabel(sale.paymentStatus)} 
                      size="small" 
                      color={getPaymentStatusColor(sale.paymentStatus) as any}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                </Grid>
                {sale.notes && (
                  <Box mt={2}>
                    <Typography variant="body2">
                      <strong>ملاحظات:</strong> {sale.notes}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              {/* إجمالي المبالغ */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="subtitle1" fontWeight="bold" color="success.dark" gutterBottom>
                  ملخص المبالغ
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">المجموع الفرعي:</Typography>
                  <Typography variant="body2">{formatCurrency(sale.total - sale.discount)}</Typography>
                </Box>
                {sale.discount > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">الخصم:</Typography>
                    <Typography variant="body2" color="error">
                      -{formatCurrency(sale.discount)}
                    </Typography>
                  </Box>
                )}
                {(sale.tax || 0) > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">الضريبة:</Typography>
                    <Typography variant="body2">{formatCurrency(sale.tax || 0)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" fontWeight="bold" color="success.dark">
                    الإجمالي:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.dark">
                    {formatCurrency(sale.total)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">المدفوع:</Typography>
                  <Typography variant="body2">{formatCurrency(sale.amountPaid || sale.total)}</Typography>
                </Box>
                {(sale.remainingAmount || 0) > 0 && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="warning.main">المتبقي:</Typography>
                    <Typography variant="body2" color="warning.main">
                      {formatCurrency(sale.remainingAmount || 0)}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* تنبيه المرتجعات */}
          {showReturnAlert && (
            <Alert severity="info" sx={{ mt: 2 }}>
              تم فتح نافذة المرتجعات لهذه الفاتورة
            </Alert>
          )}

          {/* تذييل الفاتورة */}
          <Box mt={4} textAlign="center" className="no-print">
            <Typography variant="caption" color="text.secondary">
              شكراً لتعاملكم معنا
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className="no-print">
        <Button 
          onClick={onClose}
          startIcon={<CloseIcon />}
        >
          إغلاق
        </Button>
        
        {showReturnButton && onReturn && (
          <Button
            onClick={handleReturn}
            color="warning"
            startIcon={<ReturnIcon />}
          >
            إرجاع منتجات
          </Button>
        )}
        
        <Button
          onClick={handlePrintClick}
          variant="contained"
          startIcon={<PrintIcon />}
        >
          طباعة الفاتورة
        </Button>

        {/* زر الطباعة السريعة */}
        <Button
          onClick={() => {
            const savedPrintType = localStorage.getItem('pos-print-type') as 'thermal' | 'a4' || 'a4';
            if (savedPrintType === 'thermal') {
              handleThermalPrint();
            } else {
              handleA4Print();
            }
          }}
          variant="outlined"
          size="small"
          startIcon={<PrintIcon />}
          sx={{ ml: 1 }}
        >
          طباعة سريعة
        </Button>
      </DialogActions>

      {/* قالب الطباعة الحرارية (مخفي) */}
      <Box sx={{ display: 'none' }}>
        <div ref={thermalRef}>
          <Box sx={{
            width: '76mm',
            padding: '2mm',
            fontFamily: 'Arial, sans-serif',
            fontSize: '10px',
            textAlign: 'center',
            direction: 'rtl'
          }}>
            {/* رأس الفاتورة الحرارية */}
            <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 'bold', mb: 1 }}>
              فاتورة بيع
            </Typography>
            <Typography sx={{ fontSize: '10px', mb: 1 }}>
              رقم: {sale?.invoiceNumber || sale?.id.substring(0, 8)}
            </Typography>
            <Typography sx={{ fontSize: '9px', mb: 2 }}>
              {new Date(sale?.createdAt || '').toLocaleDateString('ar-EG')}
            </Typography>

            <Divider sx={{ my: 1 }} />

            {/* معلومات العميل */}
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <Typography sx={{ fontSize: '9px' }}>
                العميل: {sale?.customerName || 'عميل نقدي'}
              </Typography>
              <Typography sx={{ fontSize: '9px' }}>
                الفرع: {sale?.branchId}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* جدول المنتجات */}
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              {sale?.items?.map((item, index) => (
                <Box key={index} sx={{ mb: 1, fontSize: '9px' }}>
                  <Typography sx={{ fontSize: '9px', fontWeight: 'bold' }}>
                    {item.name}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '8px' }}>
                      {item.quantity} × {formatCurrency(item.price)}
                    </Typography>
                    <Typography sx={{ fontSize: '8px', fontWeight: 'bold' }}>
                      {formatCurrency(item.total || (item.quantity * item.price))}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* الإجماليات */}
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '9px' }}>المجموع الفرعي:</Typography>
                <Typography sx={{ fontSize: '9px', fontWeight: 'bold' }}>
                  {formatCurrency(sale?.items?.reduce((sum, item) => sum + (item.quantity * item.price), 0) || 0)}
                </Typography>
              </Box>

              {sale?.discount && sale.discount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: '9px' }}>الخصم:</Typography>
                  <Typography sx={{ fontSize: '9px', color: 'error.main' }}>
                    -{formatCurrency(sale.discount)}
                  </Typography>
                </Box>
              )}

              {sale?.tax && sale.tax > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: '9px' }}>الضريبة:</Typography>
                  <Typography sx={{ fontSize: '9px' }}>
                    {formatCurrency(sale.tax)}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>الإجمالي:</Typography>
                <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>
                  {formatCurrency(sale?.grandTotal || sale?.total || 0)}
                </Typography>
              </Box>
            </Box>

            {/* معلومات الدفع */}
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <Typography sx={{ fontSize: '9px' }}>
                طريقة الدفع: {getPaymentMethodLabel(sale?.paymentMethod || '')}
              </Typography>
              <Typography sx={{ fontSize: '9px' }}>
                حالة الدفع: {getPaymentStatusLabel(sale?.paymentStatus || '')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* تذييل */}
            <Typography sx={{ fontSize: '8px', textAlign: 'center', mt: 2 }}>
              شكراً لتعاملكم معنا
            </Typography>
          </Box>
        </div>
      </Box>
      </Dialog>

      {/* Dialog خيارات الطباعة */}
    <Dialog
      open={showPrintOptions}
      onClose={() => setShowPrintOptions(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PrintIcon color="primary" />
          <Typography variant="h6">خيارات الطباعة</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
            اختر نوع الطباعة:
          </FormLabel>

          <RadioGroup
            value={printType}
            onChange={(e) => setPrintType(e.target.value as 'thermal' | 'a4')}
          >
            {/* خيار A4 */}
            <Card sx={{ mb: 2, border: printType === 'a4' ? '2px solid' : '1px solid', borderColor: printType === 'a4' ? 'primary.main' : 'divider' }}>
              <CardContent>
                <FormControlLabel
                  value="a4"
                  control={<Radio />}
                  label={
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <A4Icon color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          طباعة A4
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        • مناسبة للطابعات العادية
                        <br />
                        • تصميم احترافي على صفحة واحدة
                        <br />
                        • تفاصيل كاملة ومنظمة
                        <br />
                        • حجم الورق: A4 (210×297 مم)
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', width: '100%' }}
                />
              </CardContent>
            </Card>

            {/* خيار الطباعة الحرارية */}
            <Card sx={{ mb: 2, border: printType === 'thermal' ? '2px solid' : '1px solid', borderColor: printType === 'thermal' ? 'primary.main' : 'divider' }}>
              <CardContent>
                <FormControlLabel
                  value="thermal"
                  control={<Radio />}
                  label={
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <ThermalIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          طباعة حرارية
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        • مناسبة للطابعات الحرارية (80mm)
                        <br />
                        • تصميم مضغوط وسريع
                        <br />
                        • توفير في الورق والحبر
                        <br />
                        • عرض الورق: 80 مم × طول متغير
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', width: '100%' }}
                />
              </CardContent>
            </Card>
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setShowPrintOptions(false)}>
          إلغاء
        </Button>
        <Button
          onClick={handleConfirmPrint}
          variant="contained"
          startIcon={<PrintIcon />}
        >
          طباعة الآن
        </Button>
      </DialogActions>
    </Dialog>

    {/* إشعار نجاح الطباعة */}
    {showPrintSuccess && (
      <Alert
        severity="success"
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          minWidth: 300
        }}
      >
        ✅ تمت طباعة الفاتورة بنجاح!
      </Alert>
    )}

    {/* إشعار المرتجع */}
    {showReturnAlert && (
      <Alert
        severity="info"
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          minWidth: 300
        }}
      >
        📦 تم إنشاء طلب إرجاع للمنتجات
      </Alert>
    )}
    </React.Fragment>
  );
};

export default InvoiceDialog;
